'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { useEffect, useRef, useState, ImgHTMLAttributes } from 'react';
import clsx from 'clsx';
import cloudflareLoader from '@/lib/imageLoader';
import { getGlobalOptimizePatterns } from './ImageLoader';
interface LazyImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'loading'> {
  src?: string | null;
  alt: string;
  fallback?: React.ReactNode;
  onLoad?: () => void;
  onError?: () => void;
  aspectRatio?: string;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  /**
   * CSS object-position for the image (e.g. 'center', 'top', '50% 25%').
   * Controls which part of the image is kept when cropping with objectFit='cover'.
   * Defaults to 'center'.
   */
  objectPosition?: string;
  priority?: boolean;
  /** URL patterns that should use Cloudflare optimization. Falls back to global patterns. */
  optimizePatterns?: string[];
}
export function LazyImage(t0) {
  const $ = _c(65);
  let alt;
  let aspectRatio;
  let className;
  let fallback;
  let onError;
  let onLoad;
  let optimizePatterns;
  let props;
  let src;
  let style;
  let t1;
  let t2;
  let t3;
  let width;
  if ($[0] !== t0) {
    const {
      src: t4,
      alt: t5,
      fallback: t6,
      onLoad: t7,
      onError: t8,
      aspectRatio: t9,
      objectFit: t10,
      objectPosition: t11,
      priority: t12,
      optimizePatterns: t13,
      className: t14,
      style: t15,
      width: t16,
      height,
      ...t17
    } = t0;
    src = t4;
    alt = t5;
    fallback = t6;
    onLoad = t7;
    onError = t8;
    aspectRatio = t9;
    t1 = t10;
    t2 = t11;
    t3 = t12;
    optimizePatterns = t13;
    className = t14;
    style = t15;
    width = t16;
    props = t17;
    $[0] = t0;
    $[1] = alt;
    $[2] = aspectRatio;
    $[3] = className;
    $[4] = fallback;
    $[5] = onError;
    $[6] = onLoad;
    $[7] = optimizePatterns;
    $[8] = props;
    $[9] = src;
    $[10] = style;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = width;
  } else {
    alt = $[1];
    aspectRatio = $[2];
    className = $[3];
    fallback = $[4];
    onError = $[5];
    onLoad = $[6];
    optimizePatterns = $[7];
    props = $[8];
    src = $[9];
    style = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    width = $[14];
  }
  const objectFit = t1 === undefined ? "cover" : t1;
  const objectPosition = t2 === undefined ? "center" : t2;
  const priority = t3 === undefined ? false : t3;
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  let t4;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const img = imgRef.current;
      if (!img) {
        return;
      }
      if (img.complete && img.naturalWidth > 0) {
        setIsLoaded(true);
      } else {
        if (img.complete && img.naturalWidth === 0) {
          setHasError(true);
        }
      }
    };
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== src) {
    t5 = [src];
    $[16] = src;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  useEffect(t4, t5);
  let t6;
  if ($[18] !== aspectRatio || $[19] !== style) {
    t6 = {
      aspectRatio,
      ...style
    };
    $[18] = aspectRatio;
    $[19] = style;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const containerStyle = t6;
  let t7;
  if ($[21] !== src) {
    t7 = src && src.trim() !== "";
    $[21] = src;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const isValidSrc = t7;
  let t8;
  if ($[23] !== onLoad) {
    t8 = () => {
      setIsLoaded(true);
      setHasError(false);
      onLoad?.();
    };
    $[23] = onLoad;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const handleImageLoad = t8;
  let t9;
  if ($[25] !== onError) {
    t9 = () => {
      setHasError(true);
      setIsLoaded(false);
      onError?.();
    };
    $[25] = onError;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const handleImageError = t9;
  let t10;
  if ($[27] !== optimizePatterns) {
    t10 = optimizePatterns ?? getGlobalOptimizePatterns();
    $[27] = optimizePatterns;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const patternsToUse = t10;
  let t11;
  if ($[29] !== isValidSrc || $[30] !== patternsToUse || $[31] !== src) {
    t11 = isValidSrc && (!patternsToUse || patternsToUse.some(pattern => src.includes(pattern)));
    $[29] = isValidSrc;
    $[30] = patternsToUse;
    $[31] = src;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const shouldOptimize = t11;
  if (!isValidSrc || hasError && fallback) {
    let t12;
    if ($[33] !== fallback) {
      t12 = fallback || <div className="flex items-center justify-center bg-white h-full"><div className="text-center p-4"><svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg><p className="text-xs text-gray-500">Image unavailable</p></div></div>;
      $[33] = fallback;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    let t13;
    if ($[35] !== className || $[36] !== containerStyle || $[37] !== t12) {
      t13 = <div className={className} style={containerStyle}>{t12}</div>;
      $[35] = className;
      $[36] = containerStyle;
      $[37] = t12;
      $[38] = t13;
    } else {
      t13 = $[38];
    }
    return t13;
  }
  let t12;
  if ($[39] !== className) {
    t12 = clsx("relative overflow-hidden bg-gray-200", className);
    $[39] = className;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== hasError || $[42] !== isLoaded) {
    t13 = !isLoaded && !hasError && <div className="absolute inset-0 bg-white animate-pulse" />;
    $[41] = hasError;
    $[42] = isLoaded;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== alt || $[45] !== handleImageError || $[46] !== handleImageLoad || $[47] !== isLoaded || $[48] !== objectFit || $[49] !== objectPosition || $[50] !== priority || $[51] !== props || $[52] !== shouldOptimize || $[53] !== src || $[54] !== width) {
    t14 = shouldOptimize ? <Image ref={imgRef} src={src} alt={alt} fill={true} sizes={width ? `${width}px` : "100vw"} loader={cloudflareLoader} loading={priority ? "eager" : "lazy"} priority={priority} className={clsx("absolute inset-0 w-full h-full transition-opacity duration-300", isLoaded ? "opacity-100" : "opacity-0")} style={{
      objectFit,
      objectPosition
    }} onLoad={handleImageLoad} onError={handleImageError} /> : <img ref={imgRef} src={src} alt={alt} loading={priority ? "eager" : "lazy"} className={clsx("absolute inset-0 w-full h-full transition-opacity duration-300", isLoaded ? "opacity-100" : "opacity-0")} style={{
      objectFit,
      objectPosition
    }} onLoad={handleImageLoad} onError={handleImageError} {...props} />;
    $[44] = alt;
    $[45] = handleImageError;
    $[46] = handleImageLoad;
    $[47] = isLoaded;
    $[48] = objectFit;
    $[49] = objectPosition;
    $[50] = priority;
    $[51] = props;
    $[52] = shouldOptimize;
    $[53] = src;
    $[54] = width;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] !== fallback || $[57] !== hasError) {
    t15 = hasError && !fallback && <div className="absolute inset-0 flex items-center justify-center bg-white"><div className="text-center p-4"><svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg><p className="text-xs text-gray-500">Image unavailable</p></div></div>;
    $[56] = fallback;
    $[57] = hasError;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== containerStyle || $[60] !== t12 || $[61] !== t13 || $[62] !== t14 || $[63] !== t15) {
    t16 = <div className={t12} style={containerStyle}>{t13}{t14}{t15}</div>;
    $[59] = containerStyle;
    $[60] = t12;
    $[61] = t13;
    $[62] = t14;
    $[63] = t15;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  return t16;
}